// Сетка: строки, колонки, клавиатура, ввод в ячейке, вставка из буфера.
//
// ЕДИНСТВЕННАЯ сетка системы. Табель был первым её потребителем, официальный контур вторым,
// ведомость блока Б5 встаёт на неё же, поменяв каталог колонок: 281 строка против тринадцати в
// табеле и до девятнадцати колонок по шаблону объекта.
//
// ## Что здесь принципиально
//
// **Строки виртуализируются, колонки — НЕТ.** По вертикали окно обязательно: 281 строка это
// 5300 ячеек, и каждый ввод перерисовывал бы их все. По горизонтали окно вредно: оно ломает
// прижатую первую колонку (её приходится вырывать из потока), обход по Tab (следующей ячейки
// нет в разметке, фокусу некуда идти) и вставку прямоугольника из буфера. 31 колонка табеля —
// потолок, и он держится без окна.
//
// **Каретка ездит по индексу строки, а не поиском ячейки в разметке.** Под виртуализацией
// строки в разметке нет, поиск вернул бы пустоту, каретка уехала бы за экран, и человек
// печатал бы вслепую.
//
// **Ячейка, которую нельзя править, остаётся нажимаемой.** Выключенный элемент не доводит
// нажатие до обработчика: человек жмёт и не получает ничего, а со стороны это неотличимо от
// сломанной программы. Поэтому вместо `disabled` — причина по нажатию (правило 10).
//
// **Закрытый период не красится серым.** Серая заливка занята смыслом «ноль по расчёту»
// (правило 7), и слипание этих двух вещей означало бы, что человек не отличает «здесь ноль
// часов» от «здесь нельзя править». Признак закрытости — рамка и полоса состояния сверху,
// а выделение и копирование при этом работают: чтение не правка.
//
// **Размеры берутся из темы.** Высота строки, отступы ячейки и моноширинные цифры — готовый
// пресет `densityCompact` из tailwind.config.ts. Своих чисел в разметке нет.

import { useVirtualizer } from '@tanstack/react-virtual';
import type { KeyboardEvent as ReactKeyboardEvent } from 'react';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { countLabel } from '@/shared/text/plural';
import { useСреда } from '@/shared/среда';
import { cn } from '@/utils/cn';
import * as Tooltip from '@/shared/ui/tooltip';

import { applyColumnLayout, type ColumnLayout } from './layout';
import { МенюЯчейки, type ПунктМеню } from './МенюЯчейки';
import { planPaste, type PastePlan } from './paste';
import { copyText, inRange, normalizeRange, rangeSize } from './selection';
import type { CellSource, CellState, GridColumn, GridCursor } from './types';

/**
 * Высота строки в точках.
 *
 * Ровно то же число, что у `--bark-row-h` в пресете `densityCompact` (web/tailwind.config.ts):
 * виртуализация раскладывает строки по этой величине, а рисует их браузер по CSS. Разъедутся —
 * полотно поедет вниз накопительно, и внизу списка каретка встанет не на ту строку, которую
 * видно. Поэтому строка обязана быть ЖЁСТКОЙ: содержимое ячейки клиппуется, а не растягивает её.
 * Жёсткость держит пресет (`.bark-grid-cell > *` не выше строки), а проверяет её замер живым
 * браузером: `node src/shared/grid/row-height.probe.mjs`. Замер 18.08.2026 нашёл ровно этот
 * случай: двухстрочная фамилия в официальном контуре давала 33 точки вместо 28.
 */
const ВЫСОТА_СТРОКИ = 28;

/**
 * Высота строки под палец.
 *
 * 32 вместо 28, и это ровно `.density-comfortable` из того же пресета — второго числа здесь не
 * заводится. На экране планшета это три строки из двадцати пяти, зато ячейка перестаёт быть
 * полоской в 28 точек, в которую надо попадать ногтем.
 *
 * ⚠️ Обе величины обязаны меняться ВМЕСТЕ с классом плотности: виртуализация раскладывает
 * строки по числу, а рисует их браузер по переменной темы. Разъедутся — полотно поедет вниз
 * накопительно, и внизу списка каретка встанет не на ту строку, которую видно.
 */
const ВЫСОТА_СТРОКИ_ТАЧ = 32;

/**
 * Сколько строк рисуется сверх видимых.
 *
 * Восемь, а не две: колесо мыши прокручивает примерно на три строки за щелчок, и с малым
 * запасом человек видит белую полосу под курсором до следующего кадра.
 */
const ЗАПАС_СТРОК = 8;

/**
 * Сколько держать палец, чтобы открылось меню ячейки.
 *
 * Полсекунды — то же, что у системного долгого нажатия: меньше срабатывает при обычном тапе по
 * ячейке (а тап пальцем здесь начинает ввод), больше человек не дожидается и отпускает.
 */
const ДОЛГОЕ_НАЖАТИЕ = 500;

/** Сколько ячеек выделено — словами. */
const ЯЧЕЕК = ['ячейка', 'ячейки', 'ячеек'] as const;

export interface DataGridProps<TRow> {
  readonly rows: readonly TRow[];
  /** Каталог колонок целиком, до раскладки. */
  readonly columns: readonly GridColumn<TRow>[];
  /**
   * Раскладка объекта: какие колонки показывать, в каком порядке и под какими заголовками.
   *
   * Накладывается ОДИН раз, на входе: дальше по сетке ходит уже отобранный каталог, и скрытой
   * колонки не существует ни в разметке, ни в обходе клавиатурой, ни в буфере обмена. Пусто —
   * каталог как есть (табель и официальный контур шаблонов не имеют).
   */
  readonly layout?: ColumnLayout | null;
  readonly rowKey: (row: TRow) => string;
  /** Подпись строки для читалки экрана. */
  readonly rowLabel: (row: TRow) => string;
  /** Сохранить одну ячейку. Пусто — сетка только для чтения. */
  readonly onCommit?: (row: TRow, columnId: string, value: string) => void;
  /** Применить разобранную вставку целиком. */
  readonly onPaste?: (plan: PastePlan<TRow>) => void;
  /**
   * Проверка значения при вводе и при вставке: причина отказа либо null.
   *
   * Третий довод — колонка: в одной сетке колонки проверяются по-разному (рубли, процент,
   * свободный текст), и общая проверка либо пропустит мусор в деньги, либо забракует законное.
   */
  readonly validate?: (value: string, cell: CellState, columnId: string) => string | null;
  /** Нажали ячейку, которую править нельзя: показать причину. */
  readonly onBlocked?: (reason: string) => void;
  /**
   * Человек попытался ввести значение в ячейку, которая считается движком либо читается из
   * другой сущности, а каталог колонок назвал её источник: раскрыть подтаблицу.
   *
   * Сетка про маршруты НЕ знает и знать не должна — она сообщает, что и по какой сущности
   * просят раскрыть, а открывает панель экран. Обработчика нет — сетка не молчит, а называет
   * причину через `onBlocked`: молчаливый отказ неотличим от сломанной программы.
   */
  readonly onOpenSource?: (source: CellSource, context: { row: TRow; columnId: string }) => void;
  /**
   * Выделенные строки: прямоугольник выделения, сведённый к строкам.
   *
   * Сетка отдаёт строки, а не координаты: то, что снаружи хотят сделать с выделением, всегда
   * про людей в строках («присвоить состояние двенадцати»), а не про номера ячеек. Одна ячейка
   * под кареткой выделением НЕ считается — иначе полоса массового действия висела бы на экране
   * постоянно и предлагала бы «применить к одному».
   */
  readonly onSelectionChange?: (rows: readonly TRow[]) => void;
  /**
   * Свои пункты в меню ячейки — для действий над СТРОКОЙ, а не над ячейкой.
   *
   * Сетка про них ничего не знает: она отдаёт строку под кареткой и получает готовые пункты.
   * Заведено ради типа смены в табеле: он ставится на всю строку месяца, потому что человек
   * ходит на посту одним типом, а у ячейки уже есть своё единственное поведение — часы.
   * Пихать второй смысл в ячейку значило бы сломать правило четырёх поведений.
   */
  readonly menuExtras?: (row: TRow) => readonly ПунктМеню[];
  /** Почему строк нет и что делать. Обязателен: «Строк нет» — это молчание, а не состояние. */
  readonly emptyText: string;
}

export function DataGrid<TRow>({
  rows,
  columns: каталог,
  layout,
  rowKey,
  rowLabel,
  onCommit,
  onPaste,
  validate,
  onBlocked,
  onSelectionChange,
  onOpenSource,
  menuExtras,
  // Умолчания у текста пустой сетки НЕТ намеренно: «Строк нет» — это не состояние, а
  // молчание. Пустой список обязан назвать ПРИЧИНУ и следующее действие (правило 7), а
  // причина у каждой сетки своя: в ведомости не назначен никто, в периоде не заведены
  // строки, в табеле не поставлены люди. Общий текст на всех был бы верным ни для одной.
  emptyText,
}: DataGridProps<TRow>) {
  // Чем человек показывает — мышью или пальцем. Сетка это спрашивает в трёх местах, и все три
  // про попадание: высота строки, первое нажатие по ячейке и подпись сочетания клавиш.
  const среда = useСреда();
  const высотаСтроки = среда.ввод === 'тач' ? ВЫСОТА_СТРОКИ_ТАЧ : ВЫСОТА_СТРОКИ;

  // Раскладка накладывается здесь и больше нигде: ниже по файлу `columns` — это уже видимые
  // колонки в порядке шаблона. Скрытая колонка не доживает до разметки, до копирования и до
  // разбора вставки, потому что её просто нет.
  const columns = useMemo(() => applyColumnLayout(каталог, layout), [каталог, layout]);

  const [курсор, setКурсор] = useState<GridCursor | null>(null);
  // Второй угол выделения. Каретка — первый; выделение это прямоугольник между ними, а не
  // отдельный список ячеек: так его тянут в Excel, и так же он ложится обратно при вставке.
  const [якорь, setЯкорь] = useState<GridCursor | null>(null);
  // `выделить` — что делать с уже набранным при входе в ячейку. Правка существующего значения
  // выделяет его целиком (как в Excel: сразу пишешь поверх), а ввод, начатый знаком с
  // клавиатуры, — НЕТ: там этот знак уже и есть начало числа, и выделять его значит потерять.
  const [правка, setПравка] = useState<{
    курсор: GridCursor;
    значение: string;
    выделить: boolean;
  } | null>(null);
  const [ошибка, setОшибка] = useState<string | null>(null);
  // Где открыто меню ячейки, в координатах окна. `null` — закрыто. Точкой, а не элементом: у
  // меню по правой кнопке нет элемента, к которому оно прижимается.
  const [меню, setМеню] = useState<{ x: number; y: number } | null>(null);
  const корень = useRef<HTMLDivElement | null>(null);
  // Отсчёт долгого нажатия и его след. След нужен потому, что после долгого нажатия палец всё
  // равно порождает щелчок, а щелчок пальцем в этой сетке НАЧИНАЕТ ВВОД: без него человек
  // получал бы меню поверх открытого поля ввода.
  const отсчётПальца = useRef<number | null>(null);
  const былоДолгое = useRef(false);
  // Ввод закончен: и по Enter, и по Escape, и по Tab. Флаг нужен потому, что все три пути
  // возвращают фокус в сетку, а возврат фокуса синхронно гасит поле и зовёт его `onBlur` —
  // с ПРЕЖНИМ состоянием в обработчике. Без флага Escape всё равно сохранял, а Enter отправлял
  // правку на сервер ДВАЖДЫ. Поймано тестами ядра и экрана.
  const законченаПравка = useRef(false);

  //: Ключ ячейки, а не сам объект правки. По объекту зависимость срабатывала на КАЖДОМ знаке,
  //: поле переспрашивало выделение, и следующий знак затирал предыдущий: «24» превращалось в
  //: «4». Поймано тестом ядра.
  const правится =
    правка === null ? null : `${правка.курсор.rowIndex}:${правка.курсор.columnIndex}`;

  //: Фокус ставится ссылкой на элемент, а не эффектом после отрисовки. Под виртуализацией поле
  //: ввода появляется в разметке ПОЗЖЕ, чем сработал бы эффект: строка сначала въезжает в окно
  //: прокрутки и только потом рисуется. Эффект в этот момент уже отработал бы с пустой ссылкой,
  //: фокус остался бы на сетке, и следующий знак начал бы правку заново — то есть человек
  //: потерял бы набранное. Ссылка же срабатывает ровно тогда, когда поле появилось.
  //:
  //: Зависимость — ключ ячейки: пока правится та же ячейка, ссылка та же, React её не
  //: переподключает, и выделение не переспрашивается на каждом знаке.
  const выделитьПриВходе = правка?.выделить ?? false;
  const полеВвода = useCallback(
    (элемент: HTMLInputElement | null) => {
      if (элемент === null) {
        return;
      }
      элемент.focus();
      if (выделитьПриВходе) {
        элемент.select();
        return;
      }
      // Каретка в конец набранного, а не выделение: человек начал набирать «24», и второй знак
      // обязан дописаться, а не заменить первый.
      элемент.setSelectionRange(элемент.value.length, элемент.value.length);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- ключ ячейки и есть зависимость
    [правится],
  );

  // Окно строк. Колонки НЕ виртуализируются намеренно (см. шапку файла).
  const виртуализатор = useVirtualizer({
    count: rows.length,
    getScrollElement: () => корень.current,
    estimateSize: () => высотаСтроки,
    overscan: ЗАПАС_СТРОК,
    getItemKey: (индекс) => {
      const строка = rows[индекс];
      return строка === undefined ? индекс : rowKey(строка);
    },
  });

  const ячейка = useCallback(
    (позиция: GridCursor): CellState | null => {
      const строка = rows[позиция.rowIndex];
      const колонка = columns[позиция.columnIndex];
      if (строка === undefined || колонка === undefined) {
        return null;
      }
      return колонка.cell(строка);
    },
    [rows, columns],
  );

  const начатьПравку = useCallback(
    (позиция: GridCursor, начальное?: string) => {
      const состояние = ячейка(позиция);
      if (состояние === null) {
        return;
      }
      if (состояние.behaviour !== 'input') {
        const строка = rows[позиция.rowIndex];
        const колонка = columns[позиция.columnIndex];
        // Правило 6: у считаемой ячейки есть источник, и попытка ввода раскрывает его, а не
        // упирается в отказ. Куда именно вести — знает экран: сетка сообщает и умолкает.
        if (
          состояние.source !== undefined &&
          onOpenSource !== undefined &&
          строка !== undefined &&
          колонка !== undefined
        ) {
          onOpenSource(состояние.source, { row: строка, columnId: колонка.id });
          return;
        }
        // Гейтим ДЕЙСТВИЕ, а не элемент: причина обязана прозвучать по нажатию.
        onBlocked?.(состояние.reason ?? причинаПоУмолчанию(состояние.behaviour));
        return;
      }
      if (onCommit === undefined) {
        return;
      }
      // Строку ввода надо ещё и ДОВЕЗТИ до экрана. Каретка могла остаться на строке, от которой
      // человек укатил колесом мыши: под виртуализацией её в разметке нет, и поле ввода
      // появилось бы за пределами видимого — человек печатал бы вслепую.
      виртуализатор.scrollToIndex(позиция.rowIndex);
      setОшибка(null);
      законченаПравка.current = false;
      setПравка({
        курсор: позиция,
        значение: начальное ?? состояние.raw ?? '',
        выделить: начальное === undefined,
      });
    },
    [ячейка, onBlocked, onOpenSource, onCommit, rows, columns, виртуализатор],
  );

  const закончитьПравку = useCallback(
    (сохранить: boolean) => {
      if (правка === null) {
        return;
      }
      if (законченаПравка.current) {
        return;
      }
      if (!сохранить) {
        законченаПравка.current = true;
        setПравка(null);
        setОшибка(null);
        return;
      }
      const состояние = ячейка(правка.курсор);
      const строка = rows[правка.курсор.rowIndex];
      const колонка = columns[правка.курсор.columnIndex];
      if (состояние === null || строка === undefined || колонка === undefined) {
        setПравка(null);
        return;
      }
      const отказ = validate?.(правка.значение.trim(), состояние, колонка.id) ?? null;
      if (отказ !== null) {
        setОшибка(отказ);
        return;
      }
      законченаПравка.current = true;
      onCommit?.(строка, колонка.id, правка.значение.trim());
      setПравка(null);
      setОшибка(null);
    },
    [правка, ячейка, rows, columns, validate, onCommit],
  );

  const сдвинуть = useCallback(
    (по: { строк?: number; колонок?: number; расширить?: boolean }) => {
      setКурсор((прежний) => {
        const текущий = прежний ?? { rowIndex: 0, columnIndex: 0 };
        const следующий = {
          rowIndex: ограничить(текущий.rowIndex + (по.строк ?? 0), rows.length - 1),
          columnIndex: ограничить(текущий.columnIndex + (по.колонок ?? 0), columns.length - 1),
        };
        // Без Shift выделение схлопывается в одну ячейку: якорь идёт за кареткой. С Shift
        // якорь стоит там, откуда человек начал тянуть, — и остаётся стоять, пока он тянет.
        setЯкорь((прежнийЯкорь) => (по.расширить === true ? (прежнийЯкорь ?? текущий) : следующий));
        return следующий;
      });
    },
    [rows.length, columns.length],
  );

  /** Выделенный прямоугольник. Без каретки выделения нет вовсе. */
  const выделение = курсор === null ? null : normalizeRange(якорь ?? курсор, курсор);

  // Границы выделения по строкам — отдельными числами, а не объектом: объект пересоздаётся
  // каждый рендер, и подписка на него будила бы обработчик снаружи по десять раз в секунду.
  const отСтроки = выделение?.fromRow ?? -1;
  const доСтроки = выделение?.toRow ?? -1;

  useEffect(() => {
    if (onSelectionChange === undefined) {
      return;
    }
    // Одна строка выделением не считается: массовое действие над одним человеком — это то же
    // самое действие в его строке, только через две лишние полосы на экране.
    if (отСтроки < 0 || доСтроки <= отСтроки) {
      onSelectionChange([]);
      return;
    }
    onSelectionChange(rows.slice(отСтроки, доСтроки + 1));
  }, [onSelectionChange, отСтроки, доСтроки, rows]);

  /**
   * Довести колонку до видимой части по горизонтали.
   *
   * Считается по ширинам каталога, а НЕ через `scrollIntoView` по ячейке. Две причины: ячейки
   * может не быть в разметке (строка вне окна прокрутки), и `scrollIntoView` не знает про
   * прижатую первую колонку — он заводит ячейку ровно ПОД неё, и человек видит фамилию вместо
   * цифры, которую вводит.
   */
  const прокрутитьКолонку = useCallback(
    (номер: number) => {
      const элемент = корень.current;
      if (элемент === null) {
        return;
      }
      let левый = 0;
      for (let счётчик = 0; счётчик < номер; счётчик += 1) {
        левый += columns[счётчик]?.width ?? 0;
      }
      const ширина = columns[номер]?.width ?? 0;
      const прижато = columns.reduce(
        (итог, колонка) => итог + (колонка.sticky ? колонка.width : 0),
        0,
      );
      const видно = элемент.clientWidth;
      if (левый - прижато < элемент.scrollLeft) {
        элемент.scrollLeft = Math.max(левый - прижато, 0);
      } else if (левый + ширина > элемент.scrollLeft + видно) {
        элемент.scrollLeft = левый + ширина - видно;
      }
    },
    [columns],
  );

  // Каретка обязана оставаться на виду: в табеле тридцать один столбец, в ведомости 281 строка.
  // Строка доводится ПО ИНДЕКСУ через виртуализатор — искать ячейку в разметке под окном
  // прокрутки нечем, её там нет.
  //
  // Охранник по ключу каретки, а не список зависимостей эффекта: официальный контур пересобирает
  // каталог колонок на каждом рендере, и по зависимостям сетка возвращала бы человека к каретке
  // каждый раз, когда наверху обновились данные, — прямо посреди его прокрутки колесом.
  const прокручено = useRef<string | null>(null);
  useLayoutEffect(() => {
    if (курсор === null) {
      прокручено.current = null;
      return;
    }
    const ключ = `${курсор.rowIndex}:${курсор.columnIndex}`;
    if (прокручено.current === ключ) {
      return;
    }
    прокручено.current = ключ;
    виртуализатор.scrollToIndex(курсор.rowIndex);
    прокрутитьКолонку(курсор.columnIndex);
  });

  function поКлавише(событие: ReactKeyboardEvent<HTMLDivElement>) {
    if (правка !== null) {
      return;
    }
    // Клавиатурный двойник правой кнопки. Два сочетания, потому что клавиша контекстного меню
    // есть не на каждой раскладке (на ноутбуках её часто нет вовсе), а Shift+F10 работает
    // везде и означает ровно то же самое во всех программах Windows — а Windows у нас главная
    // платформа десктопа. Действие, до которого с клавиатуры не добраться, в этой сетке
    // недопустимо: её проходят стрелками целиком.
    if (событие.key === 'ContextMenu' || (событие.key === 'F10' && событие.shiftKey)) {
      событие.preventDefault();
      открытьМенюУКаретки();
      return;
    }
    // Shift со стрелкой тянет выделение, стрелка без него — переставляет каретку.
    const расширить = событие.shiftKey;
    const шаги: Record<string, () => void> = {
      ArrowDown: () => сдвинуть({ строк: 1, расширить }),
      ArrowUp: () => сдвинуть({ строк: -1, расширить }),
      ArrowRight: () => сдвинуть({ колонок: 1, расширить }),
      ArrowLeft: () => сдвинуть({ колонок: -1, расширить }),
      // Tab всегда схлопывает выделение: он про обход ячеек, а не про выбор диапазона.
      Tab: () => сдвинуть({ колонок: событие.shiftKey ? -1 : 1 }),
      Enter: () => курсор !== null && начатьПравку(курсор),
      F2: () => курсор !== null && начатьПравку(курсор),
    };
    // Tab на краю сетки НЕ перехватываем: иначе из таблицы нельзя выйти клавиатурой вовсе, а
    // это единственный способ навигации у человека, работающего без мыши.
    if (событие.key === 'Tab') {
      const край =
        курсор === null ||
        (событие.shiftKey && курсор.columnIndex === 0) ||
        (!событие.shiftKey && курсор.columnIndex === columns.length - 1);
      if (край) {
        return;
      }
    }
    const шаг = шаги[событие.key];
    if (шаг !== undefined) {
      событие.preventDefault();
      шаг();
      return;
    }
    // Автозаполнение колонки вниз (тз, раздел 5.4.4). Ctrl+D — то же сочетание, что в Excel и
    // в таблицах Гугла: табельщик приходит оттуда и жмёт его не задумываясь.
    //
    // Заполняется значением ВЕРХНЕЙ ячейки выделения, а не той, где каретка: человек мог
    // тянуть выделение снизу вверх. Идёт тем же путём, что и вставка из буфера, — планом,
    // который сначала показывают: «ляжет столько, отклонено столько и почему». Иначе одно
    // нажатие молча переписало бы двести восемьдесят одну строку, и отменить это было бы
    // нечем.
    if (
      (событие.ctrlKey || событие.metaKey) &&
      (событие.key === 'd' || событие.key === 'D' || событие.key === 'в' || событие.key === 'В')
    ) {
      if (!заполнениеДоступно) {
        return;
      }
      событие.preventDefault();
      заполнитьВниз();
      return;
    }
    // Печатаемый знак начинает ввод сразу, как в любой таблице: иначе на каждую ячейку
    // приходится лишнее нажатие, а их в месяце тридцать одно на человека.
    if (курсор !== null && событие.key.length === 1 && !событие.metaKey && !событие.ctrlKey) {
      событие.preventDefault();
      начатьПравку(курсор, событие.key);
    }
  }

  /** Можно ли сейчас заполнить вниз: есть куда писать и выделено больше одной ячейки. */
  const заполнениеДоступно =
    onPaste !== undefined && выделение !== null && rangeSize(выделение) >= 2;

  /**
   * Заполнить выделенное значением ВЕРХНЕЙ ячейки (тз, раздел 5.4.4).
   *
   * Отдельной функцией, а не только под сочетанием клавиш: на планшете клавиш Ctrl и D нет
   * вовсе, и до 10.09.2026 экран советовал нажать то, чего у человека не существует. Теперь
   * там кнопка, и код у обоих путей один — иначе кнопка и сочетание однажды разойдутся в том,
   * что именно они размножают.
   *
   * Верхняя ячейка, а не та, где каретка: выделение могли тянуть снизу вверх. Идёт тем же
   * путём, что и вставка из буфера, — планом, который сначала показывают: «ляжет столько,
   * отклонено столько и почему». Иначе одно нажатие молча переписало бы 281 строку.
   */
  function заполнитьВниз() {
    if (onPaste === undefined || выделение === null || rangeSize(выделение) < 2) {
      return;
    }
    const строк = выделение.toRow - выделение.fromRow + 1;
    const колонок = выделение.toColumn - выделение.fromColumn + 1;
    const образец = Array.from({ length: колонок }, (_, сдвиг) => {
      const колонка = columns[выделение.fromColumn + сдвиг];
      const строка = rows[выделение.fromRow];
      if (колонка === undefined || строка === undefined) {
        return '';
      }
      const ячейка = колонка.cell(строка);
      // То, что человек ПРАВИТ, а не то, что видит: в денежной ячейке это «25 934» без
      // разделителей, и размножать вниз надо именно его.
      return ячейка.raw ?? ячейка.text;
    });
    onPaste(
      planPaste({
        matrix: Array.from({ length: строк }, () => образец),
        rows,
        columns,
        rowIndex: выделение.fromRow,
        columnIndex: выделение.fromColumn,
        validate,
      }),
    );
  }

  function поВставке(событие: React.ClipboardEvent<HTMLDivElement>) {
    if (onPaste === undefined || выделение === null || правка !== null) {
      return;
    }
    const текст = событие.clipboardData.getData('text/plain');
    if (текст === '') {
      return;
    }
    событие.preventDefault();
    onPaste(
      planPaste({
        clipboard: текст,
        rows,
        columns,
        // От левого верхнего угла выделения, а не от каретки: человек мог тянуть выделение
        // снизу вверх, и каретка стоит в нижнем углу — вставка от неё легла бы не туда.
        rowIndex: выделение.fromRow,
        columnIndex: выделение.fromColumn,
        validate,
      }),
    );
  }

  /**
   * Копирование выделенного в буфер.
   *
   * Обработчик стоит на сетке, а не на кнопке: человек жмёт то же сочетание, что и в Excel, и
   * ждёт того же результата. При открытой правке событие не перехватывается — там копируется
   * содержимое поля ввода, и это ровно то, чего человек хочет.
   */
  function поКопированию(событие: React.ClipboardEvent<HTMLDivElement>) {
    if (выделение === null || правка !== null) {
      return;
    }
    событие.clipboardData.setData('text/plain', copyText({ rows, columns, range: выделение }));
    событие.preventDefault();
  }

  // ── Меню ячейки ───────────────────────────────────────────────────────────────────────────
  //
  // Почему меню вообще заведено и почему только в сетке — в шапке `МенюЯчейки.tsx`.

  /**
   * Сказать человеку, почему не вышло.
   *
   * Сначала наружу: экран показывает причину своей полосой, рядом с остальными отказами по
   * ячейке, — одно место на все объяснения, а не два разных на одном экране. Обработчика нет —
   * говорим сами и снизу сетки: молчаливый отказ неотличим от сломанной программы.
   */
  function сказать(причина: string) {
    if (onBlocked !== undefined) {
      onBlocked(причина);
      return;
    }
    setОшибка(причина);
  }

  /** Есть ли в этом окружении буфер обмена, до которого программе дали дотянуться. */
  function буферОбмена(): Clipboard | null {
    return typeof navigator === 'undefined' ? null : (navigator.clipboard ?? null);
  }

  function скопировать() {
    if (выделение === null) {
      return;
    }
    const буфер = буферОбмена();
    const текст = copyText({ rows, columns, range: выделение });
    if (буфер === null) {
      сказать(отказБуфера('скопировать', 'Ctrl+C', среда.клавиатура));
      return;
    }
    void буфер.writeText(текст).catch(() => {
      сказать(отказБуфера('скопировать', 'Ctrl+C', среда.клавиатура));
    });
  }

  function вставить() {
    if (выделение === null) {
      return;
    }
    if (onPaste === undefined) {
      сказать(ТОЛЬКО_ЧТЕНИЕ);
      return;
    }
    const буфер = буферОбмена();
    // Читаем буфер БЕЗ единого ожидания перед этим: браузер отдаёт его только внутри жеста
    // человека, а жест кончается на первом же `await`. Ровно поэтому пункт меню зовёт эту
    // функцию сразу, а не после закрытия панели с ожиданием.
    if (буфер === null) {
      сказать(отказБуфера('вставить', 'Ctrl+V', среда.клавиатура));
      return;
    }
    void буфер
      .readText()
      .then((текст) => {
        if (текст === '') {
          сказать('В буфере обмена пусто: вставлять нечего. Скопируйте нужное и повторите.');
          return;
        }
        onPaste(
          planPaste({
            clipboard: текст,
            rows,
            columns,
            // От левого верхнего угла выделения, а не от каретки: выделение могли тянуть
            // снизу вверх, и от каретки вставка легла бы не туда.
            rowIndex: выделение.fromRow,
            columnIndex: выделение.fromColumn,
            validate,
          }),
        );
      })
      .catch(() => {
        сказать(отказБуфера('вставить', 'Ctrl+V', среда.клавиатура));
      });
  }

  /**
   * Очистить выделенное.
   *
   * Идёт тем же путём, что вставка и заполнение вниз, — ПЛАНОМ, который сначала показывают:
   * «ляжет столько, отклонено столько и почему». Иначе один пункт меню молча стирал бы
   * прямоугольник на 281 строку, и вернуть его было бы нечем.
   */
  function очистить() {
    if (выделение === null) {
      return;
    }
    if (onPaste === undefined) {
      сказать(ТОЛЬКО_ЧТЕНИЕ);
      return;
    }
    const строк = выделение.toRow - выделение.fromRow + 1;
    const колонок = выделение.toColumn - выделение.fromColumn + 1;
    onPaste(
      planPaste({
        matrix: Array.from({ length: строк }, () => Array.from({ length: колонок }, () => '')),
        rows,
        columns,
        rowIndex: выделение.fromRow,
        columnIndex: выделение.fromColumn,
        validate,
      }),
    );
  }

  /**
   * Откуда взялось число под кареткой.
   *
   * Механика не новая: правило 6 уже требует, чтобы считаемая ячейка раскрывала подтаблицу-
   * источник, и до сегодня к ней вёл единственный путь — ПОПЫТКА ВВОДА в такую ячейку. То
   * есть чтобы узнать, из чего сложилось удержание, надо было сначала попробовать его
   * переписать. Пункт меню называет это действие словами и зовёт тот же обработчик.
   */
  function откудаЧисло() {
    if (курсор === null) {
      return;
    }
    const состояние = ячейка(курсор);
    const строка = rows[курсор.rowIndex];
    const колонка = columns[курсор.columnIndex];
    if (состояние === null || строка === undefined || колонка === undefined) {
      return;
    }
    if (состояние.source !== undefined && onOpenSource !== undefined) {
      onOpenSource(состояние.source, { row: строка, columnId: колонка.id });
      return;
    }
    if (состояние.behaviour === 'input') {
      сказать(
        `«${колонка.title}» — это то, что вводят руками прямо здесь. ` +
          'Ниоткуда оно не считается, источника у него нет.',
      );
      return;
    }
    сказать(состояние.reason ?? причинаПоУмолчанию(состояние.behaviour));
  }

  /** Что сейчас под меню: одна ячейка по имени или сколько их выделено. */
  function подписьМеню(): string {
    if (выделение === null || курсор === null) {
      return '';
    }
    const размер = rangeSize(выделение);
    if (размер > 1) {
      return `Выделено ${countLabel(размер, ЯЧЕЕК)}`;
    }
    const строка = rows[курсор.rowIndex];
    const колонка = columns[курсор.columnIndex];
    if (строка === undefined || колонка === undefined) {
      return '';
    }
    return `${rowLabel(строка)}, ${колонка.title}`;
  }

  const строкаПодКареткой = курсор === null ? undefined : rows[курсор.rowIndex];
  const пунктыМеню: ПунктМеню[] = [
    { подпись: 'Копировать', выполнить: скопировать },
    { подпись: 'Вставить', выполнить: вставить },
    { подпись: 'Очистить', выполнить: очистить },
    { подпись: 'Откуда это число', выполнить: откудаЧисло, черта: true },
    // Пункты экрана идут ПОСЛЕ работы с буфером и за чертой: они про другое — про строку, а
    // не про ячейку, — и стоя вперемешку с «копировать» читались бы как ещё один способ
    // что-то вставить.
    ...(строкаПодКареткой === undefined
      ? []
      : (menuExtras?.(строкаПодКареткой) ?? []).map((пункт, номер) => ({
          ...пункт,
          черта: номер === 0 ? true : пункт.черта,
        }))),
  ];

  /**
   * Открыть меню у каретки — путь клавиатуры и путь кнопки под палец.
   *
   * Координаты берутся у САМОЙ ячейки, а не у сетки: меню, открывшееся в углу таблицы, не
   * отвечает на вопрос «над чем оно», а ячейка под кареткой уже доведена до видимой части
   * (см. эффект прокрутки выше).
   */
  function открытьМенюУКаретки() {
    const место = курсор ?? { rowIndex: 0, columnIndex: 0 };
    setКурсор(место);
    setЯкорь((прежний) => прежний ?? место);
    const ячейкаВРазметке = корень.current?.querySelector(
      `[data-cell="${место.rowIndex}:${место.columnIndex}"]`,
    );
    const рамка = (
      ячейкаВРазметке instanceof HTMLElement ? ячейкаВРазметке : корень.current
    )?.getBoundingClientRect();
    setМеню({ x: рамка?.left ?? 0, y: рамка?.bottom ?? 0 });
  }

  /** Адрес ячейки под событием. `null` — событие не из ячейки: шапка, итоги, пустое поле. */
  function ячейкаПоСобытию(цель: EventTarget | null): GridCursor | null {
    if (!(цель instanceof Element)) {
      return null;
    }
    const узел = цель.closest('[data-cell]');
    const адрес = узел instanceof HTMLElement ? узел.dataset.cell : undefined;
    if (адрес === undefined) {
      return null;
    }
    const [строка, колонка] = адрес.split(':');
    const номерСтроки = Number(строка);
    const номерКолонки = Number(колонка);
    if (!Number.isInteger(номерСтроки) || !Number.isInteger(номерКолонки)) {
      return null;
    }
    return { rowIndex: номерСтроки, columnIndex: номерКолонки };
  }

  /**
   * Встать на ячейку под меню, не разрушив уже набранное выделение.
   *
   * Правая кнопка ВНУТРИ выделенного прямоугольника выделение сохраняет — так в Excel, и это
   * не мелочь: человек тянет двенадцать строк, жмёт правую кнопку и ждёт «копировать» на все
   * двенадцать. Схлопнись выделение в одну ячейку — он скопировал бы одну и узнал об этом уже
   * в своём файле.
   */
  function встатьПодМеню(место: GridCursor) {
    const внутриВыделения =
      выделение !== null &&
      rangeSize(выделение) > 1 &&
      inRange(выделение, место.rowIndex, место.columnIndex);
    if (внутриВыделения) {
      return;
    }
    setКурсор(место);
    setЯкорь(место);
  }

  function отменитьОтсчёт() {
    if (отсчётПальца.current !== null) {
      window.clearTimeout(отсчётПальца.current);
      отсчётПальца.current = null;
    }
  }

  function закрытьМеню() {
    setМеню(null);
    // Фокус обратно в сетку, а не в начало страницы: человек закрыл меню и обязан оказаться
    // там же, где был, — на своей ячейке, со стрелками, которые снова работают.
    корень.current?.focus();
  }

  if (rows.length === 0) {
    return <p className='px-4 py-6 text-paragraph-sm text-text-sub-600'>{emptyText}</p>;
  }

  const итоги = columns.map((колонка) => колонка.footer?.(rows) ?? null);
  const естьИтоги = итоги.some((значение) => значение !== null);

  // Окно строк и две распорки вместо тех, что за его пределами. Распорками, а не переводом
  // строк в абсолютное положение: абсолютная строка выпадает из таблицы, и вместе с ней
  // выпадают ширины колонок, прижатая первая колонка и шапка. Распорка же — обычная строка
  // нужной высоты, таблица остаётся таблицей.
  const окно = виртуализатор.getVirtualItems();
  const первая = окно[0];
  const последняя = окно[окно.length - 1];
  const сверху = первая?.start ?? 0;
  const снизу =
    последняя === undefined ? 0 : Math.max(виртуализатор.getTotalSize() - последняя.end, 0);

  return (
    <div
      ref={корень}
      role='grid'
      // Читалка экрана обязана знать, сколько строк ВСЕГО: в разметке их теперь не все, и без
      // этого числа она сказала бы человеку «строка 12 из 40» на ведомости в 281 строку.
      aria-rowcount={rows.length + 1}
      tabIndex={0}
      onKeyDown={поКлавише}
      onPaste={поВставке}
      onCopy={поКопированию}
      // Правая кнопка и долгое нажатие висят на КОРНЕ, а не на ячейках: событие всплывает от
      // любой из них, а пять тысяч обработчиков на ведомости в 281 строку — это пять тысяч
      // замыканий на каждую перерисовку.
      onContextMenu={(событие) => {
        const место = ячейкаПоСобытию(событие.target);
        if (место === null) {
          return;
        }
        // Своё меню вместо браузерного только там, где браузерное бесполезно: в ячейке оно
        // умеет скопировать выделенный ТЕКСТ, а прямоугольник ячеек не умеет вовсе.
        событие.preventDefault();
        встатьПодМеню(место);
        setМеню({ x: событие.clientX, y: событие.clientY });
      }}
      onPointerDown={(событие) => {
        if (событие.pointerType !== 'touch') {
          return;
        }
        const место = ячейкаПоСобытию(событие.target);
        if (место === null) {
          return;
        }
        const x = событие.clientX;
        const y = событие.clientY;
        былоДолгое.current = false;
        отменитьОтсчёт();
        отсчётПальца.current = window.setTimeout(() => {
          былоДолгое.current = true;
          встатьПодМеню(место);
          setМеню({ x, y });
        }, ДОЛГОЕ_НАЖАТИЕ);
      }}
      onPointerUp={отменитьОтсчёт}
      onPointerCancel={отменитьОтсчёт}
      onPointerMove={(событие) => {
        // Палец поехал — это прокрутка, а не долгое нажатие. Без отмены меню выскакивало бы
        // посреди пролистывания ведомости.
        if (событие.pointerType === 'touch') {
          отменитьОтсчёт();
        }
      }}
      data-прокрутка='сетка'
      // Плотность сетки выбрана осознанно и замерена: 281 строка ведомости при высоте 44
      // означала бы полотно в 12 000 точек и поиск человека прокруткой. Ячейка при этом не
      // кнопка — по ней не «нажимают», в неё встают кареткой, а цель для открытия строки даёт
      // прижатая колонка фамилии шириной в 190 точек.
      data-цель='плотная'
      className={cn(
        'тень-прокрутки relative h-full overflow-auto bg-bg-white-0 outline-none',
        среда.ввод === 'тач' ? 'density-comfortable' : 'density-compact',
      )}
    >
      {/* Провайдер подсказок — на всю сетку, один раз: у радикса он нужен выше любой
          подсказки, а заводить его на каждый заголовок значит девятнадцать провайдеров на
          ведомость. */}
      <Tooltip.Provider delayDuration={200}>
      <table className='border-separate border-spacing-0 text-grid tabular-nums'>
        <thead>
          <tr>
            {columns.map((колонка, номер) => (
              <th
                key={колонка.id}
                scope='col'
                // Опознаватель колонки в разметке: по нему экран прокручивает сетку к нужному
                // столбцу («покажи день, где не закрыто»). Другого способа найти столбец
                // снаружи нет — колонки не виртуализируются, но и ссылок наружу не отдают.
                data-column={колонка.id}
                style={{ width: колонка.width, minWidth: колонка.width }}
                className={cn(
                  'sticky top-0 z-20 h-row-head border-b border-r border-stroke-soft-200',
                  'bg-bg-weak-50 px-cell-x text-grid-head font-normal text-text-sub-600',
                  колонка.align === 'right' && 'text-right',
                  колонка.align === 'center' && 'text-center',
                  колонка.sticky && 'left-0 z-30',
                  номер === 0 && колонка.sticky && 'left-0',
                  колонка.headClassName,
                )}
              >
                {колонка.hint === undefined ? (
                  <span className='block truncate'>{колонка.title}</span>
                ) : (
                  // Заголовок с пояснением: подчёркнут точками — знак, который в таблицах
                  // читают как «здесь есть что посмотреть». Кнопка, а не просто наведение:
                  // с клавиатуры до пояснения тоже надо доходить, а мышью на 19 колонок
                  // ведомости не наводятся по очереди.
                  <Tooltip.Root>
                    <Tooltip.Trigger asChild>
                      <button
                        type='button'
                        className='block max-w-full truncate underline decoration-dotted decoration-from-font underline-offset-2 outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-base'
                      >
                        {колонка.title}
                      </button>
                    </Tooltip.Trigger>
                    <Tooltip.Content side='bottom' className='max-w-xs text-left'>
                      {колонка.hint}
                    </Tooltip.Content>
                  </Tooltip.Root>
                )}
                {колонка.subtitle !== undefined && (
                  <span className='block truncate text-text-sub-600'>{колонка.subtitle}</span>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {сверху > 0 && (
            <tr aria-hidden='true'>
              <td colSpan={columns.length} className='p-0' style={{ height: сверху }} />
            </tr>
          )}
          {окно.map(({ index: номерСтроки }) => {
            const строка = rows[номерСтроки];
            if (строка === undefined) {
              return null;
            }
            return (
              <tr key={rowKey(строка)} className='bark-grid-row' aria-rowindex={номерСтроки + 2}>
                {columns.map((колонка, номерКолонки) => {
                  const состояние = колонка.cell(строка);
                  const этаЯчейка =
                    курсор?.rowIndex === номерСтроки && курсор.columnIndex === номерКолонки;
                  const правится =
                    правка?.курсор.rowIndex === номерСтроки &&
                    правка.курсор.columnIndex === номерКолонки;
                  // Одна ячейка под кареткой выделением не считается: её и так видно по рамке, а
                  // заливка на каждой ячейке, по которой человек прошёл стрелкой, читалась бы как
                  // «здесь что-то выбрано» там, где ничего не выбрано.
                  const выделена =
                    выделение !== null &&
                    rangeSize(выделение) > 1 &&
                    inRange(выделение, номерСтроки, номерКолонки);
                  return (
                    <td
                      key={колонка.id}
                      data-cell={`${номерСтроки}:${номерКолонки}`}
                      // Поведение ячейки — правило 6, а не украшение. В разметке оно нужно затем,
                      // чтобы правило можно было ПРОВЕРИТЬ снаружи: сейчас четыре поведения
                      // различаются только оформлением, и «у заблокированной ячейки есть причина»
                      // ни один тест в настоящем браузере утверждать не может.
                      data-behaviour={состояние.behaviour}
                      style={{ width: колонка.width, minWidth: колонка.width }}
                      aria-label={`${rowLabel(строка)}, ${колонка.title}`}
                      aria-selected={выделена || этаЯчейка}
                      className={cn(
                        'bark-grid-cell border-b border-r border-stroke-soft-200 text-text-strong-950',
                        колонка.align === 'right' && 'text-right',
                        колонка.align === 'center' && 'text-center',
                        колонка.sticky && 'sticky left-0 z-10 bg-bg-white-0',
                        состояние.behaviour === 'computed' && 'text-text-sub-600',
                        // Закрытая ячейка НЕ серая: серый занят нулём по расчёту. Отличает её
                        // рамка и отсутствие каретки ввода.
                        состояние.behaviour === 'blocked' && 'border-dashed',
                        выделена && 'bg-primary-alpha-10',
                        этаЯчейка && !правится && 'ring-2 ring-inset ring-primary-base',
                        состояние.className,
                      )}
                      onClick={(событие) => {
                        // После долгого нажатия палец всё равно порождает щелчок, а щелчок
                        // пальцем в этой сетке НАЧИНАЕТ ВВОД: без этой строки человек получал
                        // бы меню поверх открытого поля ввода.
                        if (былоДолгое.current) {
                          былоДолгое.current = false;
                          return;
                        }
                        const место = { rowIndex: номерСтроки, columnIndex: номерКолонки };
                        setКурсор(место);
                        // Shift с щелчком тянет выделение от прежнего места, как в Excel: якорь
                        // остаётся там, где человек начал, и не съезжает под курсор.
                        setЯкорь((прежний) =>
                          событие.shiftKey ? (прежний ?? курсор ?? место) : место,
                        );
                        if (состояние.behaviour !== 'input') {
                          onBlocked?.(состояние.reason ?? причинаПоУмолчанию(состояние.behaviour));
                          return;
                        }
                        // Пальцем правка начинается с ПЕРВОГО нажатия, мышью — со второго.
                        //
                        // На мыши у первого щелчка есть вторая работа: он ставит якорь
                        // прямоугольного выделения под Ctrl+D и вставку из буфера. Пальцем ни
                        // того, ни другого нет — тянут выделение и вставляют иначе, — и
                        // второе нажатие остаётся ритуалом: медленным, конфликтующим с
                        // масштабированием и ниоткуда не следующим.
                        if (среда.ввод === 'тач' && !событие.shiftKey) {
                          начатьПравку(место);
                        }
                      }}
                      onDoubleClick={() =>
                        начатьПравку({ rowIndex: номерСтроки, columnIndex: номерКолонки })
                      }
                    >
                      {правится ? (
                        <input
                          ref={полеВвода}
                          value={правка.значение}
                          onChange={(событие) =>
                            setПравка({
                              курсор: правка.курсор,
                              значение: событие.target.value,
                              выделить: false,
                            })
                          }
                          onBlur={() => закончитьПравку(true)}
                          onKeyDown={(событие) => {
                            if (событие.key === 'Enter') {
                              событие.preventDefault();
                              закончитьПравку(true);
                              сдвинуть({ строк: 1 });
                              корень.current?.focus();
                            }
                            if (событие.key === 'Escape') {
                              событие.preventDefault();
                              закончитьПравку(false);
                              корень.current?.focus();
                            }
                            if (событие.key === 'Tab') {
                              событие.preventDefault();
                              закончитьПравку(true);
                              сдвинуть({ колонок: событие.shiftKey ? -1 : 1 });
                              корень.current?.focus();
                            }
                          }}
                          className={cn(
                            'h-full w-full bg-bg-white-0 text-grid tabular-nums outline-none',
                            'ring-2 ring-inset ring-primary-base',
                            колонка.align === 'right' && 'text-right',
                            колонка.align === 'center' && 'text-center',
                            ошибка !== null && 'ring-error-base',
                          )}
                          aria-invalid={ошибка !== null}
                          aria-label={`${rowLabel(строка)}, ${колонка.title}`}
                        />
                      ) : (
                        (состояние.render ?? состояние.text)
                      )}
                    </td>
                  );
                })}
              </tr>
            );
          })}
          {снизу > 0 && (
            <tr aria-hidden='true'>
              <td colSpan={columns.length} className='p-0' style={{ height: снизу }} />
            </tr>
          )}
        </tbody>
        {естьИтоги && (
          <tfoot>
            <tr className='bark-grid-row'>
              {columns.map((колонка, номер) => {
                const итог = итоги[номер];
                return (
                  <td
                    key={колонка.id}
                    style={{ width: колонка.width, minWidth: колонка.width }}
                    className={cn(
                      'bark-grid-cell sticky bottom-0 z-20 border-t border-r',
                      'border-stroke-soft-200 bg-bg-weak-50 text-label-xs text-text-strong-950',
                      колонка.align === 'right' && 'text-right',
                      колонка.align === 'center' && 'text-center',
                      колонка.sticky && 'left-0 z-30',
                      итог?.className,
                    )}
                  >
                    {итог?.render ?? итог?.text ?? ''}
                  </td>
                );
              })}
            </tr>
          </tfoot>
        )}
      </table>
      </Tooltip.Provider>
      {меню !== null && (
        <МенюЯчейки
          точка={меню}
          подпись={подписьМеню()}
          пункты={пунктыМеню}
          закрыть={закрытьМеню}
        />
      )}
      {ошибка !== null && (
        <p role='alert' className='sticky left-0 px-cell-x py-1 text-paragraph-xs text-error-base'>
          {ошибка}
        </p>
      )}
      {/* Подсказка про автозаполнение появляется ТОЛЬКО когда выделено больше одной строки, то
          есть ровно тогда, когда его и хотят. Постоянная строка внизу сетки была бы служебной
          надписью, которую через день перестают замечать, а место она ест всегда. */}
      {/* Кнопка «Ещё» под палец. Долгое нажатие остаётся, но одного его мало: жест невидим,
          и человек, который о нём не знает, о меню не узнает никогда. Показывается только на
          пальцевом вводе — на мыши её место занимает правая кнопка. */}
      {ошибка === null && среда.ввод === 'тач' && курсор !== null && (
        <div className='sticky left-0 flex items-center gap-2 px-cell-x py-1'>
          <button
            type='button'
            onClick={открытьМенюУКаретки}
            className='min-h-11 rounded-lg border border-stroke-soft-200 px-3 text-label-sm text-text-strong-950 transition duration-200 ease-out hover:bg-bg-weak-50'
          >
            Ещё
          </button>
        </div>
      )}
      {ошибка === null && onPaste !== undefined && выделение !== null && доСтроки > отСтроки && (
        <div className='sticky left-0 flex items-center gap-2 px-cell-x py-1'>
          {среда.клавиатура && (
            <span className='text-paragraph-xs text-text-sub-600'>
              Ctrl+D заполнит выделенное значением верхней ячейки
            </span>
          )}
          {/* Пальцем сочетания клавиш не нажать, и до 10.09.2026 автозаполнения на планшете не
              существовало вовсе — экран советовал нажать Ctrl+D. Кнопка делает ровно то же
              самое и тем же кодом: показывает план «ляжет столько, отклонено столько». */}
          {среда.ввод === 'тач' && (
            <button
              type='button'
              onClick={заполнитьВниз}
              className='min-h-11 rounded-lg border border-stroke-soft-200 px-3 text-label-sm text-text-strong-950 transition duration-200 ease-out hover:bg-bg-weak-50'
            >
              Заполнить вниз
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Почему меню не может вставить и очистить, когда ввод не включён.
 *
 * Названо СОСТОЯНИЕ, а не запрет: «нельзя» отвечает на вопрос «можно ли», а человек спрашивает
 * «почему не работает». Кнопку включения меню не называет намеренно — она у каждого экрана
 * своя, и обещать чужое слово значит однажды соврать.
 */
const ТОЛЬКО_ЧТЕНИЕ =
  'Ввод в эту таблицу сейчас не включён, поэтому вставлять и очищать нечем. ' +
  'Включите правку наверху экрана — и оба действия заработают.';

/**
 * Отказ буфера обмена — с тем, что делать дальше.
 *
 * Сочетание клавиш называется ТОЛЬКО тому, кому есть чем его нажать: на планшете «нажмите
 * Ctrl+C» — это совет нажать то, чего у человека не существует.
 */
function отказБуфера(действие: string, сочетание: string, естьКлавиатура: boolean): string {
  const шаг = естьКлавиатура
    ? ` То же самое делает ${сочетание} — нажмите его.`
    : ' Повторите попытку: доступ к буферу выдаёт система, а не программа.';
  return `Не вышло ${действие}: система не дала программе доступ к буферу обмена.${шаг}`;
}

function ограничить(значение: number, предел: number): number {
  return Math.max(0, Math.min(значение, Math.max(предел, 0)));
}

function причинаПоУмолчанию(поведение: CellState['behaviour']): string {
  if (поведение === 'computed') {
    return 'Это значение считается системой, вводить его не нужно.';
  }
  if (поведение === 'linked') {
    return 'Значение берётся из другой карточки. Откройте её, чтобы поправить.';
  }
  // Сюда попадает только `blocked` без своей причины — то есть каталог колонок причину забыл.
  // Два слова «Править нельзя» читаются как поломка: человек не знает ни почему, ни к кому
  // идти. Пока причины нет, честнее сказать, что её обязаны были написать, и назвать, у кого
  // спросить, — а не делать вид, что объяснение дано.
  return (
    'Эту ячейку сейчас править нельзя, а причина к ней не подписана. ' +
    'Так быть не должно: сообщите об этом финансовому отделу, они передадут разработчикам.'
  );
}
